import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import SearchDropdown from './components/SearchDropdown';
import AudioPlayer, { AudioPlayerRef } from './AudioPlayer';
import RoundTracker from './components/RoundTracker';
import { AudioClipService } from './services/AudioClipService';

// Enable the debug panel only when the app is running locally (e.g., during development)
// We treat the environment as local if the hostname is `localhost` or `127.0.0.1`.
// Using `typeof window !== 'undefined'` guards against potential server-side environments.
const DEV_MODE = typeof window !== 'undefined' && (
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1'
);

// Create a single instance of the audio clip service
const audioClipService = new AudioClipService();

// Converts a 2-letter ISO country code to its corresponding flag emoji
const countryCodeToFlag = (code: string): string => {
  if (!code || code.length !== 2) return '';
  return String.fromCodePoint(
    ...code.toUpperCase().split('').map(c => 127397 + c.charCodeAt(0))
  );
};

interface ChallengeElement {
  code: string;
  url: string;
}

interface ChallengeData {
  lastUpdated: string;
  elements: ChallengeElement[];
}

function App() {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [debugDate, setDebugDate] = useState<string>(() => {
    const storedDate = localStorage.getItem('debugDate');
    return storedDate || new Date().toISOString().slice(0, 10);
  });
  const [languages, setLanguages] = useState<string[]>([]);
  const [languageInfoMap, setLanguageInfoMap] = useState<Record<string, { name: string; family: string; countryCode: string; hello?: string }>>({});
  const [isDebugPanelHidden, setIsDebugPanelHidden] = useState<boolean>(false);
  const [hint1Used, setHint1Used] = useState<boolean>(false);
  const [hint2Used, setHint2Used] = useState<boolean>(false);
  const [hint3Used, setHint3Used] = useState<boolean>(false);
  // Track which hints are currently showing temporary animation
  const [animatingHints, setAnimatingHints] = useState<{hint1: boolean, hint2: boolean, hint3: boolean}>({
    hint1: false,
    hint2: false,
    hint3: false
  });
  const [currentClipIndex, setCurrentClipIndex] = useState<number>(0);
  const [guessHistory, setGuessHistory] = useState<{ 
    guessedLanguage: string; 
    correctLanguage: string; 
    hintsUsed: number;
    replaysUsed: number;
  }[]>([]);
  // Track whether to display the results table once the game is over
  const [showResults, setShowResults] = useState<boolean>(() => {
    const stored = localStorage.getItem('showResults');
    return stored ? JSON.parse(stored) as boolean : false;
  });
  const [resultsCopied, setResultsCopied] = useState<boolean>(false);
  const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
  // State for challenge data loaded from challenge.json
  // Initialize with fallback clips so app can render immediately
  const [audioClips, setAudioClips] = useState<Record<string, { url: string; languageCode: string }>>(() => {
    return audioClipService.getAllClips();
  });
  const [clipIds, setClipIds] = useState<string[]>(() => {
    return audioClipService.getAllClipIds();
  });
  const [challengeLoaded, setChallengeLoaded] = useState<boolean>(false);

  // Reference to the audio player for resetting its position
  const audioPlayerRef = useRef<AudioPlayerRef>(null);

  // Helper to determine if the user is currently on the last available clip
  const isLastClip = clipIds.length > 0 ? currentClipIndex === clipIds.length - 1 : false;

  // Reference to the "Next clip" button so we can programmatically click it
  const nextButtonRef = React.useRef<HTMLButtonElement>(null);

  // Load challenge.json with S3 presigned URLs
  useEffect(() => {
    fetch(process.env.PUBLIC_URL + '/challenge.json')
      .then(res => res.json())
      .then((data: ChallengeData) => {
        // Check if this is a new challenge by comparing lastUpdated timestamp
        const storedChallengeDate = localStorage.getItem('challengeDate');
        const isNewChallenge = storedChallengeDate !== data.lastUpdated;
        
        if (isNewChallenge) {
          // New challenge detected - clear all game state
          console.log('New challenge detected, clearing game state');
          localStorage.removeItem('selectedItems');
          localStorage.removeItem('currentClipIndex');
          localStorage.removeItem('guessHistory');
          localStorage.removeItem('showResults');
          localStorage.removeItem('challengeDate');
          
          // Reset state
          setSelectedItems([]);
          setCurrentClipIndex(0);
          setGuessHistory([]);
          setShowResults(false);
          setHint1Used(false);
          setHint2Used(false);
          setHint3Used(false);
          setAnimatingHints({ hint1: false, hint2: false, hint3: false });
          
          // Show info modal for new challenge
          setShowInfoModal(true);
        }
        
        // Store the current challenge date
        localStorage.setItem('challengeDate', data.lastUpdated);
        
        // Convert challenge.json elements to AudioClips format
        // Generate UUIDs for each element
        const clips: Record<string, { url: string; languageCode: string }> = {};
        const ids: string[] = [];
        
        data.elements.forEach((element, index) => {
          // Generate a simple UUID-like ID based on index and code
          const clipId = `${index}-${element.code}-${Date.now()}`;
          clips[clipId] = {
            url: element.url,
            languageCode: element.code
          };
          ids.push(clipId);
        });
        
        setAudioClips(clips);
        setClipIds(ids);
        setChallengeLoaded(true);
      })
      .catch(err => {
        console.error('Failed to load challenge.json:', err);
        // Fallback to hardcoded clips if challenge.json fails to load
        const fallbackClips = audioClipService.getAllClips();
        const fallbackIds = audioClipService.getAllClipIds();
        setAudioClips(fallbackClips);
        setClipIds(fallbackIds);
        setChallengeLoaded(true);
      });
  }, []);

  // Load language names from languageInfo.json instead of languages.txt
  useEffect(() => {
    fetch(process.env.PUBLIC_URL + '/languageInfo.json')
      .then(res => res.json())
      .then((data: Record<string, { name: string; family: string; countryCode: string; hello?: string }>) => {
        setLanguageInfoMap(data);
        const names = Array.from(
          new Set(
            Object.values(data)
              .map(info => info.name)
              .filter(Boolean)
          )
        ).sort();
        setLanguages(names);
      })
      .catch(err => {
        console.error('Failed to load languageInfo.json:', err);
      });
  }, []);

  useEffect(() => {
    const hideUntil = localStorage.getItem('hideDebugUntil');
    if (hideUntil) {
      if (new Date().getTime() < new Date(hideUntil).getTime()) {
        setIsDebugPanelHidden(true);
        const timeout = new Date(hideUntil).getTime() - new Date().getTime();
        setTimeout(() => {
          setIsDebugPanelHidden(false);
          localStorage.removeItem('hideDebugUntil');
        }, timeout);
      } else {
        localStorage.removeItem('hideDebugUntil');
      }
    }

    // Only load game state after challenge has been loaded and validated
    // This prevents loading stale state before challenge check completes
    // The challenge loading useEffect will handle clearing state for new challenges
  }, [challengeLoaded]);

  // Load game state from localStorage after challenge is loaded
  useEffect(() => {
    if (!challengeLoaded) return;

    const storedChallengeDate = localStorage.getItem('challengeDate');
    
    // Only load game state if we have a stored challenge date
    if (storedChallengeDate) {
      const storedItems = localStorage.getItem('selectedItems');
      if (storedItems) {
        setSelectedItems(JSON.parse(storedItems));
      }
      const storedClipIndex = localStorage.getItem('currentClipIndex');
      if (storedClipIndex) {
        setCurrentClipIndex(parseInt(storedClipIndex, 10));
      }
      const storedGuessHistory = localStorage.getItem('guessHistory');
      if (storedGuessHistory) {
        setGuessHistory(JSON.parse(storedGuessHistory));
      } else {
        // No play history found – show the information modal to first-time visitors
        setShowInfoModal(true);
      }
    } else {
      // No challenge date stored - show info modal for first-time visitors
      setShowInfoModal(true);
    }
  }, [challengeLoaded]);

  // Global key listener to make Enter trigger the Next clip button when visible
  useEffect(() => {
    const handleGlobalEnter = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;

      const isEditableElement =
        (document.activeElement &&
          ((document.activeElement.tagName === 'INPUT' &&
            (document.activeElement as HTMLInputElement).type === 'text') ||
            document.activeElement.tagName === 'TEXTAREA' ||
            (document.activeElement as HTMLElement).isContentEditable)) ||
        false;

      if (isEditableElement) return;

      if (nextButtonRef.current && nextButtonRef.current.offsetParent !== null) {
        e.preventDefault();
        nextButtonRef.current.click();
      }
    };

    window.addEventListener('keydown', handleGlobalEnter);
    return () => {
      window.removeEventListener('keydown', handleGlobalEnter);
    };
  }, [selectedItems, showResults]);

  // Guard against accessing clips before they're loaded
  if (clipIds.length === 0 || !audioClips[clipIds[currentClipIndex]]) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
  }

  const currentClip = audioClips[clipIds[currentClipIndex]];
  const path = currentClip.url;

  // Derive language details from the loaded languageInfo.json
  const langInfo = languageInfoMap[currentClip.languageCode] || {};
  const currentLanguageName = (langInfo as any).name;
  const currentLanguageFamily = (langInfo as any).family || '';
  const currentCountryCode = countryCodeToFlag((langInfo as any).countryCode || '');
  const currentGreeting = (langInfo as any).hello || '';
  // Determine if the user has made at least one guess for the current clip
  const hasGuessed = selectedItems.length > 0;

  // Hints should be revealed either when individually used or when the user has guessed at least once
  const revealHint1 = hint1Used || hasGuessed;
  const revealHint2 = hint2Used || hasGuessed;
  const revealHint3 = hint3Used || hasGuessed;

  // Debug: Clear cache handler
  const handleClearCache = () => {
    localStorage.clear();
    setSelectedItems([]);
    setGuessHistory([]);
    setCurrentClipIndex(0);
    setShowResults(false);
    setHint1Used(false);
    setHint2Used(false);
    setHint3Used(false);
    setAnimatingHints({ hint1: false, hint2: false, hint3: false });
    // Reset audio player position to zero
    audioPlayerRef.current?.reset();
  };

  // Debug: Set date handler
  const handleSetDate = (e: React.FormEvent) => {
    e.preventDefault();
    localStorage.setItem('debugDate', debugDate);
    alert(`Debug date set to: ${debugDate}`);
  };

  const handleUseHint1 = () => {
    setHint1Used(true);
    setAnimatingHints(prev => ({ ...prev, hint1: true }));
    setTimeout(() => setAnimatingHints(prev => ({ ...prev, hint1: false })), 500);
  };

  const handleUseHint2 = () => {
    setHint2Used(true);
    setAnimatingHints(prev => ({ ...prev, hint2: true }));
    setTimeout(() => setAnimatingHints(prev => ({ ...prev, hint2: false })), 500);
  };

  const handleUseHint3 = () => {
    setHint3Used(true);
    setAnimatingHints(prev => ({ ...prev, hint3: true }));
    setTimeout(() => setAnimatingHints(prev => ({ ...prev, hint3: false })), 500);
  };

  // Move to the next clip when there are more clips remaining
  const handleNextClip = () => {
    if (isLastClip) return; // Safety guard – this button shouldn't appear on last clip

    const nextIndex = currentClipIndex + 1;
    setCurrentClipIndex(nextIndex);
    localStorage.setItem('currentClipIndex', nextIndex.toString());
    localStorage.removeItem('selectedItems');
    setSelectedItems([]);
    setHint1Used(false);
    setHint2Used(false);
    setHint3Used(false);
    setAnimatingHints({ hint1: false, hint2: false, hint3: false });
    setShowResults(false);
    localStorage.removeItem('showResults');
  };

  // Explicitly reveal the results table once the user presses the Show Results button
  const handleShowResults = () => {
    setShowResults(true);
    localStorage.setItem('showResults', 'true');
  };

  // Calculate the user's score (point-based system)
  const calculateScore = () => {
    const basePointsPerRound = 500; // 2500 total / 5 rounds
    const hintPenalty = 100; // points deducted per hint used
    const replayPenalty = 50; // points deducted per replay
    
    return guessHistory.reduce((totalScore, guess) => {
      // Start with base points if guess is correct, 0 if incorrect
      let roundScore = guess.guessedLanguage === guess.correctLanguage ? basePointsPerRound : 0;
      
      // Deduct points for hints and replays (only if the guess was correct)
      if (roundScore > 0) {
        roundScore -= (guess.hintsUsed * hintPenalty);
        roundScore -= (guess.replaysUsed * replayPenalty);
        // Ensure score doesn't go below 0 for this round
        roundScore = Math.max(0, roundScore);
      }
      
      return totalScore + roundScore;
    }, 0);
  };

  // Share results via Web Share API or copy to clipboard as fallback
  const handleShareResults = async () => {
    const score = calculateScore();
    const emojiGrid = guessHistory
      .map(guess => guess.guessedLanguage === guess.correctLanguage ? '🟩' : '🟥')
      .join('');

    const textToShare = `Lingguessr ${new Date(debugDate.replace(/-/g, '/')).toLocaleDateString()}

${score} points

${emojiGrid}

Play at https://lingguessr.vercel.app/`;

    // Try Web Share API first (only if it exists and we can share text)
    if (navigator.share && navigator.canShare && navigator.canShare({ text: textToShare })) {
      try {
        await navigator.share({
          title: 'Lingguessr Results',
          text: textToShare,
        });
        return; // Success, we're done
      } catch (err) {
        // Fall through to clipboard
      }
    } else if (navigator.share) {
      try {
        await navigator.share({
          title: 'Lingguessr Results',
          text: textToShare,
        });
        return; // Success, we're done
      } catch (err) {
        // Fall through to clipboard
      }
    }

    // Try clipboard as fallback (requires HTTPS and user gesture)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(textToShare);
        setResultsCopied(true);
        setTimeout(() => setResultsCopied(false), 3000);
        return; // Success, we're done
      } catch (err) {
        // Fall through to legacy clipboard or alert
      }
    }

    // Try legacy clipboard method
    try {
      const textArea = document.createElement('textarea');
      textArea.value = textToShare;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      textArea.setSelectionRange(0, 99999); // For mobile devices
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        setResultsCopied(true);
        setTimeout(() => setResultsCopied(false), 3000);
        return; // Success, we're done
      }
    } catch (err) {
      // Fall through to final alert
    }

    // Final fallback: alert
    alert(textToShare);
  };

  const handleHideDebugPanel = () => {
    const hideUntil = new Date(new Date().getTime() + 5 * 60000); // 5 minutes from now
    localStorage.setItem('hideDebugUntil', hideUntil.toISOString());
    setIsDebugPanelHidden(true);

    setTimeout(() => {
      setIsDebugPanelHidden(false);
      localStorage.removeItem('hideDebugUntil');
    }, 5 * 60000);
  };

  return (
    <div className="main-wrapper">
      {DEV_MODE && !isDebugPanelHidden && (
        <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 1000, padding: '1rem', background: '#f5f5f5', borderRadius: 8, border: '1px solid #ddd' }}>
          <strong>Dev Debug Panel</strong>
          <div style={{ marginTop: 8 }}>
            <button onClick={handleClearCache} style={{ marginRight: 12 }}>Clear Cache</button>
            <form onSubmit={handleSetDate} style={{ display: 'inline', marginRight: 12 }}>
              <input
                type="date"
                value={debugDate}
                onChange={e => setDebugDate(e.target.value)}
                style={{ marginRight: 8 }}
              />
              <button type="submit">Set Date</button>
            </form>
            <button onClick={handleHideDebugPanel}>Hide for 5 mins</button>
          </div>
        </div>
      )}
      
      {/* Info button in upper right */}
      <button 
        className="info-button"
        onClick={() => setShowInfoModal(true)}
        aria-label="Information about the game"
      >
        i
      </button>

      {/* Info Modal */}
      {showInfoModal && (
        <div 
          className="info-modal-overlay"
          onClick={() => setShowInfoModal(false)}
        >
          <div 
            className="info-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="info-modal-close"
              onClick={() => setShowInfoModal(false)}
              aria-label="Close modal"
            >
              ×
            </button>
            
            <h2>Welcome to Lingguessr!</h2>
            <br/>
            <div>
              <h3>🎯 Objective</h3>
              <p>Listen to audio clips of different languages and guess which language is being spoken!</p>
              <br/>
              <h3>🎮 How to Play</h3>
              <ol>
                <li><strong>Listen:</strong> Click play to hear an audio clip in an unknown language</li>
                <li><strong>Get Hints:</strong> Click the hint cards to reveal clues (e.g. language family, country of origin, traditional greeting)</li> 
                <li><strong>Guess:</strong> Type your guess in the search box</li>
                <li><strong>Share:</strong> At the end, share your results!</li>
              </ol>
              <br/>
              <h3>📖 About</h3>
              <p>Lingguessr is developed by <a href="https://gbmg.dev" target="_blank" rel="noopener noreferrer">Gabe Magee</a>. Voice clips are sourced from the <a href="https://commonvoice.mozilla.org/" target="_blank" rel="noopener noreferrer">Mozilla Common Voice dataset</a>. 
                If you have comments, questions, complaints or bugs, please reach out to me at gabe [at] gbmg [dot] dev</p>
              <br/>
              <p><strong>New puzzles daily!</strong> Come back tomorrow for a fresh set of languages to identify.</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="container dropdown-container">
        <div className="header-section">
          <div className="title-section">
            <h1 className="main-title">Lingguessr</h1>
            <h2 className="subtitle">{new Date(debugDate.replace(/-/g, '/')).toLocaleDateString()}</h2>
          </div>
          <RoundTracker 
            currentRound={currentClipIndex}
            totalRounds={clipIds.length}
            guessHistory={guessHistory}
          />
        </div>
        <div className="app-body">
          {showResults ? (
            <div className="results-full-screen">
              <div className="results-container">
                <table className="results-table">
                  <thead>
                    <tr>
                      <th>Spoken Language</th>
                      <th>Your Guess</th>
                      <th>Hints Used</th>
                      <th>Replays</th>
                    </tr>
                  </thead>
                  <tbody>
                    {guessHistory.map((guess, index) => {
                      const correct = guess.guessedLanguage === guess.correctLanguage;
                      return (
                        <tr key={index}>
                          <td>{guess.correctLanguage}</td>
                          <td style={{ color: correct ? '#4fa703' : '#e74c3c' }}>
                            {guess.guessedLanguage}
                          </td>
                          <td>{guess.hintsUsed}</td>
                          <td>{guess.replaysUsed}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="score-and-share">
                  <div className="final-score">
                    <h3>Your Score</h3>
                    <div className="score-display">
                      {calculateScore()} points
                    </div>
                  </div>
                  {/* Share button styles are now inline to avoid Safari iOS issues */}
                  <div 
                    onClick={handleShareResults}
                    role="button"
                    tabIndex={0}
                    style={{ 
                      backgroundColor: 'var(--accent-green)', 
                      padding: '1rem 1.5rem', 
                      margin: '1rem auto 0',
                      color: '#fff',
                      fontSize: '1.3rem',
                      textAlign: 'center',
                      border: 'none',
                      cursor: 'pointer',
                      borderRadius: '8px',
                      width: '280px',
                      maxWidth: '100%',
                      display: 'block',
                      boxSizing: 'border-box'
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleShareResults();
                      }
                    }}
                  >
                    {resultsCopied ? 'Results copied to clipboard!' : 'Share your results!'}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="guessing-panel">
                <AudioPlayer ref={audioPlayerRef} key={currentClipIndex} src={path} className="spaced-below"/>
                <div className="hint-container">
                  <div 
                    className={`hint-card ${revealHint1 ? 'revealed' : ''} ${animatingHints.hint1 ? 'animating' : ''}`}
                    onClick={!revealHint1 ? handleUseHint1 : undefined}
                  >
                    {revealHint1 ? currentLanguageFamily : "Language Family"}
                  </div>
                  <div 
                    className={`hint-card ${revealHint2 ? 'revealed' : ''} ${animatingHints.hint2 ? 'animating' : ''}`}
                    onClick={!revealHint2 ? handleUseHint2 : undefined}
                  >
                    {revealHint2 ? <span style={{ fontSize: '2em' }}>{currentCountryCode}</span> : "Country of Origin"}
                  </div>
                  <div
                    className={`hint-card ${revealHint3 ? 'revealed' : ''} ${animatingHints.hint3 ? 'animating' : ''}`}
                    onClick={!revealHint3 ? handleUseHint3 : undefined}
                  >
                    {revealHint3 ? currentGreeting + "!" : "Greeting"}
                  </div>
                </div>
              </div>
              <div className="search-panel">
                {selectedItems.length > 0 ? (
                  <div className="guess-placeholder">
                    {selectedItems[0] === currentLanguageName ? (
                      <h2>Correct!<br/><br/>The spoken language was {currentLanguageName}</h2>
                    ) : (
                      <h2>Incorrect!<br/><br/>The spoken language was {currentLanguageName}</h2>
                    )}

                    {isLastClip ? (
                      <button
                        ref={nextButtonRef}
                        className="next-clip-button"
                        onClick={handleShowResults}
                        aria-label="Show results (press Enter)"
                      >
                        Show results <span className="enter-hint">(Enter)</span>
                      </button>
                    ) : (
                      <button
                        ref={nextButtonRef}
                        className="next-clip-button"
                        onClick={handleNextClip}
                        aria-label="Next clip (press Enter)"
                      >
                        Next clip <span className="enter-hint">(Enter)</span>
                      </button>
                    )}
                  </div>
                ) : (
                  <SearchDropdown
                    options={languages}
                    placeholder="Type to guess a language..."
                    onSelect={(item) => {
                      const hintsUsed = (hint1Used ? 1 : 0) + (hint2Used ? 1 : 0) + (hint3Used ? 1 : 0);
                      const replaysUsed = audioPlayerRef.current?.getReplayCount() || 0;
                      const newHistory = [...guessHistory, { 
                        guessedLanguage: item, 
                        correctLanguage: currentLanguageName,
                        hintsUsed,
                        replaysUsed
                      }];
                      setGuessHistory(newHistory);
                      localStorage.setItem('guessHistory', JSON.stringify(newHistory));
                      localStorage.setItem('selectedItems', JSON.stringify([...selectedItems, item]));
                      setSelectedItems([...selectedItems, item]);
                    }}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default App; 